import { useId, useState } from 'react';
import styled from 'styled-components';
import { Avatar, Button, MetaRow, Panel, Tabs } from '@/ui';
import { Icon } from '@/icons';
import { PostCardSurface } from '@/components/PostCard/PostCardSurface';
import { ComposerFrame } from '@/features/home/composer/ComposerFrame';
import { ComposerTool } from '@/features/home/composer/ComposerTool';
import { TrendingPanelView } from '@/components/panels/TrendingPanel';
import { DiscoveryPromo } from '@/components/panels/DiscoveryPromo';
import { SurfaceScope } from '@/theme/SurfaceScope';

const MODES = [
  { value: 'post', label: 'Post' },
  { value: 'composer', label: 'Composer' },
  { value: 'sidebar', label: 'Sidebar' },
];
const Frame = styled.div`
  pointer-events: none;
  user-select: none;
  margin-top: 12px;
  min-width: 0;
`;
const Label = styled.p`
  margin: 0 0 6px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  small {
    display: block;
    color: ${({ theme }) => theme.color.textMuted};
  }
`;
const Body = styled.p`
  margin: 8px 0;
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.5;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 8px 0;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textMuted};
  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
`;
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;
const ComposerSurface = styled(Panel).attrs({ $surfaceRole: 'overlay' })`
  h3 {
    margin: 0 0 16px;
    font-size: ${({ theme }) => theme.fontSize.md};
  }
  textarea {
    width: 100%;
    box-sizing: border-box;
    font: inherit;
    color: inherit;
    resize: none;
  }
  ${Actions} > button {
    margin-left: auto;
  }
  [data-overlay='true'] [data-tools] {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

function SampleIdentity() {
  return (
    <Identity>
      <Avatar name="Alex Rivera" size={36} />
      <div>
        <strong>Alex Rivera</strong>
        <small>
          <MetaRow>
            <span>@alex</span>
            <span>1h</span>
          </MetaRow>
        </small>
      </div>
    </Identity>
  );
}

/** Presentation only: no feed/session hooks, draft stores, uploads or action services. */
export function AppearancePreview() {
  const [mode, setMode] = useState('post');
  const id = useId();
  return (
    <div>
      <Label>Live preview</Label>
      <Tabs
        items={MODES}
        value={mode}
        onChange={setMode}
        ariaLabel="Preview surface"
        idPrefix={id}
      />
      {MODES.map(({ value }) => (
        <div
          key={value}
          role="tabpanel"
          id={`${id}-panel-${value}`}
          aria-labelledby={`${id}-tab-${value}`}
          hidden={mode !== value}
        >
          {mode === value && (
            <Frame inert data-testid="appearance-preview">
              {mode === 'post' && (
                <PostCardSurface>
                  <SampleIdentity />
                  <Body>
                    Sunsets, good friends, and better conversations. Sharing a few moments with this
                    community. #TruXSocial
                  </Body>
                  <Actions>
                    {(['comment', 'cherish', 'heart', 'share'] as const).map((icon, i) => (
                      <span key={icon}>
                        <Icon name={icon} size={16} />
                        {i < 3 ? [12, 8, 42][i] : null}
                      </span>
                    ))}
                  </Actions>
                </PostCardSurface>
              )}
              {mode === 'composer' && (
                <ComposerSurface>
                  <h3>Create a post</h3>
                  <ComposerFrame $overlay data-overlay="true">
                    <SampleIdentity />
                    <textarea
                      aria-label="Sample post"
                      readOnly
                      value="What made you smile today?"
                    />
                    <div data-tools>
                      {(
                        [
                          ['image', 'Add image'],
                          ['bar-chart-3', 'Add poll'],
                          ['smile', 'Add emoji'],
                          ['calendar-clock', 'Schedule post'],
                          ['at-sign', 'Mention'],
                          ['map-pin', 'Location'],
                        ] as const
                      ).map(([icon, label]) => (
                        <ComposerTool key={label} icon={icon} label={label} />
                      ))}
                    </div>
                    <Actions>
                      <span>
                        <Icon name="globe" size={16} />
                        Everyone
                      </span>
                      <Button variant="primary">Post</Button>
                    </Actions>
                  </ComposerFrame>
                </ComposerSurface>
              )}
              {mode === 'sidebar' && (
                <SurfaceScope role="panel">
                  <Stack>
                    <TrendingPanelView
                      trends={[
                        { tag: '#CreativeVoices', count: '1.2K posts' },
                        { tag: '#SmallMoments', count: '840 posts' },
                      ]}
                    />
                    <DiscoveryPromo />
                  </Stack>
                </SurfaceScope>
              )}
            </Frame>
          )}
        </div>
      ))}
    </div>
  );
}
